/**
 * @file ActivitiesPage.tsx
 * @description Страница «Задачи» кабинета магазина (phase19.31, SC60;
 * crm.md T1–T7) — рабочий список «что сделать» вместо прежних «Активностей».
 *
 * Только задачи, звонки и встречи (заметки, письма, SMS — в лентах клиента
 * и сделки). Раскладка: заголовок с «+ Задача», строка отборов
 * («Мои / Все / Просрочено / Без исполнителя» со счётчиками сервера, виды,
 * «Показать выполненные» — всё в адресе), список по группам срока по поясу
 * магазина. Флажок слева отмечает выполненной (исполнитель, автор, владелец —
 * T4) — в уведомлении «Отменить» возвращает её в работу (контракт §13);
 * нажатие на строку открывает окно задачи.
 *
 * Адрес страницы прежний (`crm/activities`) — меняется только подпись.
 */

import { CircleCheckBig, ListChecks, Plus, RefreshCw, Search, UsersRound, type LucideIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useParams } from "react-router";

import { ActivityComposerDialog } from "../../../../components/crm/deal-card/ActivityComposerDialog";
import { groupTasks } from "../../../../components/crm/tasks/task-groups";
import { completeTaskWithUndo } from "../../../../components/crm/tasks/task-mutations";
import { canCompleteTask } from "../../../../components/crm/tasks/task-permissions";
import { TaskDetailDialog } from "../../../../components/crm/tasks/TaskDetailDialog";
import { TaskGroupList } from "../../../../components/crm/tasks/TaskGroupList";
import { TasksFilterBar } from "../../../../components/crm/tasks/TasksFilterBar";
import { resetTasksFilters, tasksEmptyText, type TaskScope } from "../../../../components/crm/tasks/tasks-query";
import { useShopToday } from "../../../../components/crm/tasks/useShopToday";
import { useTasksPage } from "../../../../components/crm/tasks/useTasksPage";
import { useTaskViewer } from "../../../../components/crm/tasks/useTaskViewer";
import { Button } from "../../../../components/ui/button";
import { CabinetPageHeader } from "../../../../components/ui/CabinetPageHeader";
import { ListEmpty, ListError, ListSkeleton } from "../../../../components/ui/ListStates";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import type { PageAction } from "../../../../components/ui/page-actions";
import { PHONE_QUERY, useMediaQuery } from "../../../../hooks/useMediaQuery";
import type { ActivityDto } from "../../../../lib/api-crm";
import { getShopTimeZone } from "../../../../lib/shop-time";

/** Значок пустого списка по отбору; под фильтром — поиск. */
const EMPTY_ICON: Record<TaskScope, LucideIcon> = {
  mine: ListChecks,
  all: ListChecks,
  overdue: CircleCheckBig,
  unassigned: UsersRound,
};

export function ActivitiesPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = `/shop/${slug}`;
  const notify = useNotify();
  const viewer = useTaskViewer();
  const phone = useMediaQuery(PHONE_QUERY);
  const page = useTasksPage();
  const { query, setQuery } = page;
  const timeZone = getShopTimeZone();
  const [opened, setOpened] = useState<ActivityDto | null>(null);
  const [composing, setComposing] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);

  // Группы пересчитываются при новых данных и при смене дня в поясе магазина
  // (вкладку оставили на ночь — «Завтра» становится «Сегодня» при возврате).
  const today = useShopToday(timeZone);
  const groups = useMemo(
    () => groupTasks(page.items, Math.max(Date.now(), today.now), timeZone),
    [page.items, timeZone, today.now],
  );
  const empty = tasksEmptyText(query);

  async function complete(task: ActivityDto) {
    setCompletingId(task.id);
    // Уведомление с «Отменить» — возврат в работу (POST …/reopen).
    await completeTaskWithUndo(task.id, { notify, reload: page.refresh });
    setCompletingId(null);
  }

  const menu: PageAction[] = [
    { key: "refresh", label: "Обновить список", icon: RefreshCw, run: page.refresh, loading: page.loading },
  ];
  const createButton = viewer.canUseCrm ? (
    <Button variant="brand" onClick={() => setComposing(true)}>
      <Plus className="size-4" />
      Задача
    </Button>
  ) : undefined;

  const hasItems = page.items.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader title="Задачи" menu={menu} menuLabel="Другие действия с задачами" action={createButton} />

      <TasksFilterBar query={query} counts={page.counts} onChange={setQuery} />

      {page.error ? <ListError message={page.error} onRetry={page.refresh} /> : null}

      {!hasItems && page.loading ? <ListSkeleton rows={8} compact={phone} /> : null}

      {!hasItems && !page.loading && !page.error ? (
        <ListEmpty
          filtered={empty.filtered}
          spec={{
            icon: empty.filtered ? Search : EMPTY_ICON[query.scope],
            title: empty.title,
            description: empty.description,
            onReset: empty.filtered ? () => setQuery(resetTasksFilters(query)) : undefined,
          }}
        />
      ) : null}

      {hasItems ? (
        <TaskGroupList
          groups={groups}
          timeZone={timeZone}
          base={base}
          canComplete={(task) => canCompleteTask(task, viewer)}
          completingId={completingId}
          onComplete={(task) => void complete(task)}
          onOpen={setOpened}
        />
      ) : null}

      {page.total > page.items.length ? (
        <p className="type-caption text-on-surface-variant">
          Показаны первые {page.items.length} из {page.total}. Сузьте отбор, чтобы увидеть остальные.
        </p>
      ) : null}

      {opened ? (
        <TaskDetailDialog
          task={opened}
          viewer={viewer}
          timeZone={timeZone}
          base={base}
          onClose={() => setOpened(null)}
          onChanged={page.refresh}
        />
      ) : null}

      {composing ? (
        <ActivityComposerDialog
          kind="TASK"
          onClose={() => setComposing(false)}
          onSaved={() => {
            setComposing(false);
            page.refresh();
          }}
        />
      ) : null}
    </div>
  );
}
